import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
} from '@chakra-ui/react';
import { QUERY_MATCHES, GET_MATCH_RESULTS, GET_MATCH_FORMAT } from '../../../Queries';

export const DisciplineResults = ({ discipline, disciplineName }) => {
  const [selectedMatchId, setSelectedMatchId] = useState(null);

  // Query completed matches for this discipline
  const { data: matchesData, loading: matchesLoading } = useQuery(QUERY_MATCHES, {
    variables: {
      status: 'COMPLETED',
      match_discipline: discipline,
    },
  });

  // Query cancelled matches for this discipline
  const { data: cancelledData, loading: cancelledLoading } = useQuery(QUERY_MATCHES, {
    variables: {
      status: 'CANCELLED',
      match_discipline: discipline,
    },
  });

  // Combine completed and cancelled matches
  const allMatches = [
    ...(matchesData?.queryMatches || []),
    ...(cancelledData?.queryMatches || [])
  ].sort((a, b) => b.match_details.match_date - a.match_details.match_date);

  // Query results for selected match
  const { data: resultsData, loading: resultsLoading } = useQuery(GET_MATCH_RESULTS, {
    variables: { match_id: selectedMatchId },
    skip: !selectedMatchId,
  });

  // Get format ID from selected match
  const selectedMatch = allMatches.find(m => m.match_id === selectedMatchId);
  const formatId = selectedMatch?.match_details?.match_format_id;
  const isCancelled = selectedMatch?.match_details?.match_status === 'CANCELLED';

  // Query match format to get tie-breaker rules
  const { data: formatData } = useQuery(GET_MATCH_FORMAT, {
    variables: { format_id: formatId },
    skip: !formatId,
  });

  const matches = allMatches;
  const results = resultsData?.getMatchResults || [];
  const format = formatData?.getMatchFormat?.format_details;

  // Group results by division and category with tie-breaker logic
  const groupedResults = React.useMemo(() => {
    const groups = {};
    results.forEach((result) => {
      const division = result.result_details.division || 'No Division';
      const category = result.result_details.category || 'No Category';
      const key = `${division}|${category}`;

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(result);
    });

    // Sort each group by aggregate_score descending and apply tie-breakers
    Object.keys(groups).forEach((key) => {
      const group = groups[key];
      
      // For MATCH_POINTS, calculate match points for each competitor
      if (format?.aggregation_method === 'MATCH_POINTS' && format?.stages) {
        // First, find the maximum hit factor for each stage
        const stageMaxHF = {};
        const stagePoints = {};
        
        format.stages.forEach(stage => {
          let maxHF = 0;
          const stagePointsValue = parseFloat(stage.stage_points) || 100;
          stagePoints[stage.stage_name] = stagePointsValue;
          
          group.forEach(result => {
            const stageScore = result.result_details.stage_scores?.find(s => s.stage_name === stage.stage_name);
            if (stageScore) {
              const hitFactor = stageScore.calculated_value || 0;
              if (hitFactor > maxHF) {
                maxHF = hitFactor;
              }
            }
          });
          
          stageMaxHF[stage.stage_name] = maxHF;
        });
        
        // Calculate match points for each competitor
        group.forEach(result => {
          let totalMatchPoints = 0;
          const matchPointsBreakdown = [];
          
          format.stages.forEach(stage => {
            const stageScore = result.result_details.stage_scores?.find(s => s.stage_name === stage.stage_name);
            const hitFactor = stageScore?.calculated_value || 0;
            const maxHF = stageMaxHF[stage.stage_name];
            const stagePointsValue = stagePoints[stage.stage_name];
            
            let matchPoints = 0;
            if (maxHF > 0) {
              matchPoints = (hitFactor / maxHF) * stagePointsValue;
            }
            
            totalMatchPoints += matchPoints;
            matchPointsBreakdown.push({
              stage_name: stage.stage_name,
              hit_factor: hitFactor,
              match_points: matchPoints,
              is_winner: hitFactor === maxHF && maxHF > 0
            });
          });
          
          // Store calculated match points in the result
          result.result_details.aggregate_score = totalMatchPoints;
          result.result_details.match_points_breakdown = matchPointsBreakdown;
        });
      }
      
      // Sort by aggregate_score descending (higher is better)
      group.sort((a, b) => {
        const detailsA = a.result_details;
        const detailsB = b.result_details;
        const scoreA = detailsA.aggregate_score;
        const scoreB = detailsB.aggregate_score;
        
        // If scores are different, sort by score
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }
        
        // Scores are tied - apply tie-breaker rules if available
        if (format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps?.length > 0) {
          for (const step of format.tiebreaker_rules.steps) {
            if (step.type === 'AGGREGATE_SCORE_FIELD') {
              const fieldName = `total_${step.field}`;
              const valueA = detailsA[fieldName] || 0;
              const valueB = detailsB[fieldName] || 0;
              
              if (valueA !== valueB) {
                // Apply direction (DESC = higher wins, ASC = lower wins)
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            } else if (step.type === 'STAGE_SCORE') {
              // Find the specific stage score
              const stageA = detailsA.stage_scores?.find(s => s.stage_name === step.stage_id);
              const stageB = detailsB.stage_scores?.find(s => s.stage_name === step.stage_id);
              const valueA = stageA?.calculated_value || 0;
              const valueB = stageB?.calculated_value || 0;
              
              if (valueA !== valueB) {
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            } else if (step.type === 'SHOOTOFF') {
              // Check if shootoff data exists
              const shootoffField = `shootoff_${step.order}_score`;
              const valueA = detailsA.tiebreaker_data?.[shootoffField];
              const valueB = detailsB.tiebreaker_data?.[shootoffField];
              
              if (valueA !== undefined && valueB !== undefined && valueA !== valueB) {
                return valueB - valueA; // Higher shootoff score wins
              }
            } else if (step.type === 'CUSTOM_FIELD') {
              // Check custom field in tiebreaker_data
              const valueA = detailsA.tiebreaker_data?.custom_fields?.[step.field] || 0;
              const valueB = detailsB.tiebreaker_data?.custom_fields?.[step.field] || 0;
              
              if (valueA !== valueB) {
                return step.direction === 'DESC' ? valueB - valueA : valueA - valueB;
              }
            }
            // SHARED_PLACEMENT doesn't affect sorting, just rank display
          }
        }
        
        // Still tied after all tie-breakers
        return 0;
      });
      
      // Assign ranks within the group, handling ties
      let currentRank = 1;
      for (let i = 0; i < group.length; i++) {
        const result = group[i];
        const details = result.result_details;
        
        // Check if this result is tied with the previous one
        let isTied = false;
        let rankNote = null;
        
        if (i > 0) {
          const prevResult = group[i - 1];
          const prevDetails = prevResult.result_details;
          
          if (details.aggregate_score === prevDetails.aggregate_score) {
            isTied = true;
            
            // Check if tie was broken by tie-breaker rules
            if (format?.tiebreaker_rules?.enabled && format.tiebreaker_rules.steps?.length > 0) {
              for (const step of format.tiebreaker_rules.steps) {
                if (step.type === 'AGGREGATE_SCORE_FIELD') {
                  const fieldName = `total_${step.field}`;
                  const valueA = details[fieldName] || 0;
                  const valueB = prevDetails[fieldName] || 0;
                  
                  if (valueA !== valueB) {
                    // Tie was broken - add rank note
                    rankNote = `${valueA} ${step.label}`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'STAGE_SCORE') {
                  const stageA = details.stage_scores?.find(s => s.stage_name === step.stage_id);
                  const valueA = stageA?.calculated_value || 0;
                  
                  const stageB = prevDetails.stage_scores?.find(s => s.stage_name === step.stage_id);
                  const valueB = stageB?.calculated_value || 0;
                  
                  if (valueA !== valueB) {
                    rankNote = `${valueA} (${step.label})`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'SHOOTOFF') {
                  const shootoffField = `shootoff_${step.order}_score`;
                  const valueA = details.tiebreaker_data?.[shootoffField];
                  const valueB = prevDetails.tiebreaker_data?.[shootoffField];
                  
                  if (valueA !== undefined && valueB !== undefined && valueA !== valueB) {
                    rankNote = `${valueA}/${step.target_count || '?'} SO`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'CUSTOM_FIELD') {
                  const valueA = details.tiebreaker_data?.custom_fields?.[step.field] || 0;
                  const valueB = prevDetails.tiebreaker_data?.custom_fields?.[step.field] || 0;
                  
                  if (valueA !== valueB) {
                    rankNote = `${valueA} ${step.label}`;
                    isTied = false;
                    break;
                  }
                } else if (step.type === 'SHARED_PLACEMENT') {
                  // Explicitly mark as tied
                  rankNote = step.label || 'Tied';
                  isTied = true;
                  break;
                }
              }
            }
          }
        }
        
        // If not tied with previous, increment rank
        if (!isTied && i > 0) {
          currentRank = i + 1;
        }
        
        // Create new object with rank and rankNote
        group[i] = {
          ...result,
          rank: isTied ? `T${currentRank}` : currentRank,
          rankNote: rankNote
        };
      }
    });

    return groups;
  }, [results, format]);

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (matchesLoading || cancelledLoading) {
    return (
      <Box mt={6} textAlign="center">
        <Spinner size="lg" />
        <Text mt={4}>Loading match results...</Text>
      </Box>
    );
  }

  if (matches.length === 0) {
    return (
      <Box mt={6}>
        <Heading size="md" mb={4}>
          {disciplineName} Match Results
        </Heading>
        <Text color="gray.600" mb={4}>
          No completed matches yet. Results will be posted here after each competition.
        </Text>
        <Text fontSize="sm">
          For more information, contact{' '}
          <Link href="mailto:competitions@goldengunclub.us" color="blue.500">
            competitions@goldengunclub.us
          </Link>
        </Text>
      </Box>
    );
  }

  return (
    <Box mt={6}>
      <Heading size="md" mb={4}>
        {disciplineName} Match Results
      </Heading>

      <VStack align="stretch" spacing={6}>
        {/* Match Selector */}
        <Box>
          <Text fontWeight="medium" mb={2}>
            Select a match to view results:
          </Text>
          <Select
            placeholder="Choose a match..."
            value={selectedMatchId || ''}
            onChange={(e) => setSelectedMatchId(e.target.value)}
            size="lg"
          >
            {matches.map((match) => {
              const isCancelled = match.match_details.match_status === 'CANCELLED';
              return (
                <option key={match.match_id} value={match.match_id}>
                  {isCancelled ? '🚫 CANCELLED - ' : ''}{match.match_details.match_name} - {formatDate(match.match_details.match_date)}
                </option>
              );
            })}
          </Select>
        </Box>

        {/* Results Display */}
        {selectedMatchId && (
          <Box>
            {resultsLoading ? (
              <Box textAlign="center" py={8}>
                <Spinner size="lg" />
                <Text mt={4}>Loading results...</Text>
              </Box>
            ) : results.length === 0 ? (
              <Box bg="yellow.50" p={4} borderRadius="md">
                <Text>No results available for this match yet.</Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={6}>
                {/* Match Info */}
                {selectedMatch && (
                  <Box 
                    bg={isCancelled ? 'red.50' : 'blue.50'} 
                    p={4} 
                    borderRadius="md"
                    borderWidth={isCancelled ? '2px' : '0'}
                    borderColor={isCancelled ? 'red.300' : 'transparent'}
                  >
                    <VStack align="stretch" spacing={2}>
                      {isCancelled && (
                        <Badge colorScheme="red" fontSize="md" mb={2}>
                          ⚠️ MATCH CANCELLED
                        </Badge>
                      )}
                      <HStack justify="space-between" flexWrap="wrap">
                        <Box>
                          <Text 
                            fontWeight="bold" 
                            fontSize="lg"
                            textDecoration={isCancelled ? 'line-through' : 'none'}
                            color={isCancelled ? 'red.600' : 'inherit'}
                          >
                            {selectedMatch.match_details.match_name}
                          </Text>
                          <Text fontSize="sm" color="gray.600">
                            {formatDate(selectedMatch.match_details.match_date)}
                          </Text>
                        </Box>
                        {!isCancelled && (
                          <Badge colorScheme="green" fontSize="md">
                            {results.length} Participants
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {/* Results by Division/Category */}
                <Accordion allowMultiple defaultIndex={[0]}>
                  {Object.keys(groupedResults).map((key) => {
                    const [division, category] = key.split('|');
                    const groupResults = groupedResults[key];

                    return (
                      <AccordionItem key={key}>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <Text fontWeight="bold">
                                {division} - {category}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {groupResults.length} {groupResults.length === 1 ? 'participant' : 'participants'}
                              </Text>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <Table variant="striped" size="sm">
                            <Thead>
                              <Tr>
                                <Th>Place</Th>
                                <Th>Participant</Th>
                                {format?.aggregation_method === 'MATCH_POINTS' && format?.stages?.map(stage => (
                                  <Th key={stage.stage_name} isNumeric>
                                    {stage.stage_name}
                                    <Text fontSize="xs" fontWeight="normal" color="gray.600">
                                      HF / Pts
                                    </Text>
                                  </Th>
                                ))}
                                <Th isNumeric>
                                  {format?.aggregation_method === 'MATCH_POINTS' ? 'Total Match Points' : 'Score'}
                                </Th>
                                {format?.aggregation_method !== 'MATCH_POINTS' && <Th>Tie-Breaker</Th>}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {groupResults.map((result) => (
                                <Tr key={result.result_id}>
                                  <Td fontWeight="bold">
                                    {result.rank === 1 && '🥇 '}
                                    {result.rank === 2 && '🥈 '}
                                    {result.rank === 3 && '🥉 '}
                                    {result.rank}
                                  </Td>
                                  <Td>{result.result_details.participant_name}</Td>
                                  {format?.aggregation_method === 'MATCH_POINTS' && result.result_details.match_points_breakdown?.map((stageData, idx) => (
                                    <Td key={idx} isNumeric>
                                      <VStack spacing={0} align="end">
                                        <Text fontSize="sm" fontWeight={stageData.is_winner ? 'bold' : 'normal'}>
                                          {stageData.hit_factor.toFixed(2)}
                                          {stageData.is_winner && ' 🏆'}
                                        </Text>
                                        <Text fontSize="xs" color="gray.600">
                                          {stageData.match_points.toFixed(1)}
                                        </Text>
                                      </VStack>
                                    </Td>
                                  ))}
                                  <Td isNumeric fontWeight="bold">
                                    {result.result_details.aggregate_score.toFixed(2)}
                                  </Td>
                                  {format?.aggregation_method !== 'MATCH_POINTS' && (
                                    <Td>
                                      {result.rankNote ? (
                                        <Text fontSize="sm" color="gray.600">
                                          {result.rankNote}
                                        </Text>
                                      ) : (
                                        <Text fontSize="sm" color="gray.400">
                                          -
                                        </Text>
                                      )}
                                    </Td>
                                  )}
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </VStack>
            )}
          </Box>
        )}

        {/* Contact Info */}
        <Box fontSize="sm" color="gray.600">
          For questions about match results, contact{' '}
          <Link href="mailto:competitions@goldengunclub.us" color="blue.500">
            competitions@goldengunclub.us
          </Link>
        </Box>
      </VStack>
    </Box>
  );
};
